import {
  LifecycleError,
  LifecycleService,
} from "../../../services/Integrations/LifecycleService.ts";
import type {
  Advisory,
  CanonicalPolicy,
  Installment,
  IssueRequest,
  IssueResponse,
  PaymentRequest,
  PaymentResponse,
} from "../../../types/lifecycle.ts";

type PendingPayment = {
  kind: "payment";
  request: PaymentRequest;
  installmentKey: string;
};
type PendingOperation = { kind: "issue"; request: IssueRequest } | PendingPayment;
export interface OperationState {
  pending: PendingOperation | null;
  issue: IssueResponse | null;
  issuedPolicy: CanonicalPolicy | null;
  issuedAdvisories: Advisory[];
  payments: { key: string; response: PaymentResponse }[];
  /**
   * A payment the insurer accepted while the local write failed. Only resending
   * the same request (same messageId) is allowed: a new one would pay again. It
   * keeps the insurer's receipt number, so recovery does not depend on the error.
   */
  localWriteRetry: (PendingPayment & { receiptNumber: string | null }) | null;
  busy: boolean;
  error: unknown;
  inventoryRevision: number;
}

// OZK's unpaid-only schedule renumbers remaining rows after payment.
export const installmentKey = (
  policy: CanonicalPolicy,
  row: Installment
): string =>
  JSON.stringify([
    policy.provider,
    policy.policyNumber,
    policy.provider === "ozk"
      ? [row.dueDate, row.amount.amount, row.amount.currency]
      : row.sequence,
  ]);
export const isPayable = (row: Installment): boolean =>
  row.status === "due" && Boolean(row.paymentTarget) && row.amount.amount > 0;

/** Current-page requests and API results only; nothing is stored in the browser. */
export class LifecycleOperations {
  private state: OperationState = {
    pending: null,
    issue: null,
    issuedPolicy: null,
    issuedAdvisories: [],
    payments: [],
    localWriteRetry: null,
    busy: false,
    error: null,
    inventoryRevision: 0,
  };
  private listeners = new Set<() => void>();

  subscribe = (listener: () => void) => {
    this.listeners.add(listener);
    return () => {
      this.listeners.delete(listener);
    };
  };
  getSnapshot = () => this.state;
  private update(patch: Partial<OperationState>) {
    this.state = { ...this.state, ...patch };
    this.listeners.forEach((listener) => listener());
  }
  clearError = () => this.update({ error: null });
  get blocked() {
    return this.state.busy;
  }
  async issue(jwt: string, request: Omit<IssueRequest, "messageId">) {
    if (this.blocked) return;
    await this.execute(jwt, {
      kind: "issue",
      request: {
        ...structuredClone(request),
        messageId: LifecycleService.newMessageId(),
      },
    });
  }
  async pay(
    jwt: string,
    policy: CanonicalPolicy,
    row: Installment,
    request: Omit<PaymentRequest, "messageId">
  ) {
    const key = installmentKey(policy, row);
    if (
      this.blocked ||
      this.state.localWriteRetry ||
      !isPayable(row) ||
      this.state.payments.some((payment) => payment.key === key)
    )
      return;
    await this.execute(jwt, {
      kind: "payment",
      installmentKey: key,
      request: {
        ...structuredClone(request),
        messageId: LifecycleService.newMessageId(),
      },
    });
  }
  /** Resend the accepted payment unchanged so the gateway replays its result. */
  async retryLocalWrite(jwt: string) {
    const retry = this.state.localWriteRetry;
    if (this.blocked || !retry) return;
    await this.execute(jwt, retry);
  }
  private async execute(jwt: string, pending: PendingOperation) {
    this.update({ pending, busy: true, error: null });
    try {
      if (pending.kind === "issue") {
        const response = await LifecycleService.issuePolicy(
          jwt,
          pending.request
        );
        this.update({
          issue: response,
          issuedPolicy: response.policy,
          issuedAdvisories: response.advisories,
        });
      } else {
        const response = await LifecycleService.reportPayment(
          jwt,
          pending.request
        );
        this.update({
          payments: [
            ...this.state.payments,
            { key: pending.installmentKey, response },
          ],
          localWriteRetry: null,
        });
      }
    } catch (error) {
      this.update({ error });
      if (
        error instanceof LifecycleError &&
        error.code === "LOCAL_POLICY_WRITE_FAILED"
      ) {
        this.update({
          issuedPolicy: error.issued?.policy ?? this.state.issuedPolicy,
          issuedAdvisories: error.issued?.advisories ?? [],
        });
      }
      if (
        pending.kind === "payment" &&
        error instanceof LifecycleError &&
        error.code === "LOCAL_PAYMENT_WRITE_FAILED"
      ) {
        this.update({
          localWriteRetry: {
            ...pending,
            receiptNumber:
              error.paid?.receipt.receiptNumber ??
              this.state.localWriteRetry?.receiptNumber ??
              null,
          },
        });
      }
    } finally {
      this.update({
        pending: null,
        busy: false,
        inventoryRevision: this.state.inventoryRevision + 1,
      });
    }
  }
}
